/**
 * Métricas de GRUPO (ticket médio, ROI médio) sobre uma coleção arbitrária de
 * empresas — usadas pelo filtro de plano do modal de PDF do SegmentStatsCard,
 * que combina planos livremente (o backend só resolve "por plano" isolado, em
 * roi.byPlanType). Extraído pra módulo próprio pra ser testável sem importar
 * o componente React inteiro.
 *
 * Mesmas fórmulas do backend (company-metrics.aggregate.ts / benchmark-stats.ts)
 * — duplicadas aqui de propósito porque frontend e backend são bundles
 * separados (Vite vs Node); ver FDN-1939, achado #8.
 */

/** Percentil por interpolação linear — mesma fórmula de benchmark-stats.ts (backend). */
export function percentileLocal(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null
  if (sorted.length === 1) return sorted[0]
  const idx = (sorted.length - 1) * p
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sorted[lo]
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

/** Winsorized Mean — mesma fórmula de company-metrics.aggregate.ts (backend). */
export function winsorizedMeanLocal(rois: number[]): number | null {
  if (rois.length === 0) return null
  const sorted = [...rois].sort((a, b) => a - b)
  const p10 = percentileLocal(sorted, 0.1)
  const p90 = percentileLocal(sorted, 0.9)
  if (p10 === null || p90 === null) return null
  const clipped = rois.map(v => Math.min(Math.max(v, p10), p90))
  return clipped.reduce((s, v) => s + v, 0) / clipped.length
}

/**
 * Ticket médio de um GRUPO qualquer de empresas — mesma fórmula de
 * company-metrics.aggregate.ts (backend).
 */
export function computeGroupTicketMedio(items: Array<{
  totalOrders: number
  totalRevenue: number
  influencedOrders: number
  influencedRevenue: number
  attributedOrdersRevenue: number
}>) {
  const totalOrders = items.reduce((s, c) => s + c.totalOrders, 0)
  const totalRevenue = items.reduce((s, c) => s + c.totalRevenue, 0)
  const influencedOrders = items.reduce((s, c) => s + c.influencedOrders, 0)
  const influencedRevenue = items.reduce((s, c) => s + c.influencedRevenue, 0)
  const attributedOrdersRevenue = items.reduce((s, c) => s + c.attributedOrdersRevenue, 0)

  const avgTicket = totalOrders > 0 ? totalRevenue / totalOrders : 0
  const avgTicketInfluenced = influencedOrders > 0 ? influencedRevenue / influencedOrders : 0
  const avgTicketAttributedOrders = influencedOrders > 0 ? attributedOrdersRevenue / influencedOrders : 0
  const organicOrders = totalOrders - influencedOrders
  const organicRevenue = totalRevenue - attributedOrdersRevenue
  const avgTicketOrganic = organicOrders > 0 ? organicRevenue / organicOrders : 0
  const diffPercent = avgTicketOrganic > 0
    ? ((avgTicketAttributedOrders - avgTicketOrganic) / avgTicketOrganic) * 100
    : null

  return { avgTicket, avgTicketInfluenced, avgTicketAttributedOrders, avgTicketOrganic, diffPercent }
}

/**
 * ROI médio de um GRUPO qualquer de empresas, nas duas versões publicadas sem
 * hierarquia (FDN-1939, achado #2):
 * - avgRoi: média dos `roiAdjusted` já calculados pelo backend (Split-SD
 *   relativo ao segmento INTEIRO) — mesma convenção do roi.byPlanType do
 *   backend, que também não re-encolhe por plano, só reagrupa o que já foi ajustado.
 * - avgRoiWinsorized: recalculada de fato sobre o subconjunto filtrado (os
 *   percentis p10/p90 são do próprio grupo filtrado, não do segmento inteiro).
 */
export function computeGroupRoi(items: Array<{ roi: number | null; roiAdjusted?: number | null }>) {
  const withRoi = items.filter((c): c is { roi: number; roiAdjusted?: number | null } => c.roi !== null)
  const adjusted = withRoi
    .map(c => c.roiAdjusted)
    .filter((v): v is number => v !== null && v !== undefined)
  const avgRoi = adjusted.length > 0 ? adjusted.reduce((a, b) => a + b, 0) / adjusted.length : null
  const avgRoiWinsorized = winsorizedMeanLocal(withRoi.map(c => c.roi))
  return { avgRoi, avgRoiWinsorized, companiesWithRoi: withRoi.length }
}
